import { tKey, type I18nKey } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/modules/shadcn/ui/alert";
import {
  ModelAiSuggestion,
  SuggestionWarning,
  SuggestionWarningCodeEnum,
  SuggestionWarningSeverityEnum,
} from "@repo/schema";
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  Info,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const WARNING_STYLES: Record<
  SuggestionWarningSeverityEnum,
  { className: string; Icon: typeof AlertTriangle }
> = {
  [SuggestionWarningSeverityEnum.CRITICAL]: {
    className: "border-red-200 bg-red-50 text-red-800",
    Icon: AlertCircle,
  },
  [SuggestionWarningSeverityEnum.WARNING]: {
    className: "border-amber-200 bg-amber-50 text-amber-800",
    Icon: AlertTriangle,
  },
  [SuggestionWarningSeverityEnum.INFO]: {
    className: "border-blue-200 bg-blue-50 text-blue-800",
    Icon: Info,
  },
};

/**
 * API-authored warnings carry a code and render from the catalog in the
 * reader's language; model-authored ones, and snapshots from before codes,
 * show their frozen `message`.
 */
const WARNING_CODE_KEYS = {
  [SuggestionWarningCodeEnum.LABEL_SELECTION_FALLBACK]: tKey(
    "model:aiNotes.warningCodes.LABEL_SELECTION_FALLBACK",
  ),
  [SuggestionWarningCodeEnum.LABELS_EXCLUDED]: tKey(
    "model:aiNotes.warningCodes.LABELS_EXCLUDED",
  ),
} satisfies Record<SuggestionWarningCodeEnum, I18nKey<"model">>;

export const WarningAlert = ({ warning }: { warning: SuggestionWarning }) => {
  const { t } = useTranslation(["model"]);
  const { className, Icon } = WARNING_STYLES[warning.severity];
  const title = warning.code
    ? t(WARNING_CODE_KEYS[warning.code], warning.params)
    : warning.message;
  return (
    <Alert className={cn("py-2", className)}>
      <Icon className="size-4" />
      <AlertTitle className="text-sm font-medium">{title}</AlertTitle>
      {warning.affectedLabels && warning.affectedLabels.length > 0 && (
        <AlertDescription>
          <div className="flex flex-row flex-wrap gap-1 pt-1">
            {warning.affectedLabels.map((label) => (
              <span
                key={label}
                className="rounded-full border border-current/30 px-2 text-xs leading-5"
              >
                {label}
              </span>
            ))}
          </div>
        </AlertDescription>
      )}
    </Alert>
  );
};

const SEVERITY_ORDER = [
  SuggestionWarningSeverityEnum.CRITICAL,
  SuggestionWarningSeverityEnum.WARNING,
  SuggestionWarningSeverityEnum.INFO,
];

const COUNT_BADGE_STYLES: Record<SuggestionWarningSeverityEnum, string> = {
  [SuggestionWarningSeverityEnum.CRITICAL]: "bg-red-100 text-red-800",
  [SuggestionWarningSeverityEnum.WARNING]: "bg-amber-100 text-amber-800",
  [SuggestionWarningSeverityEnum.INFO]: "bg-blue-100 text-blue-800",
};

/**
 * The user-facing outcome of an AI suggestion run: the summary paragraph and
 * the generated warnings, collapsed behind a chevron by default. Shown on
 * the new-version form right after the suggestion is applied and again on
 * the model detail page from the persisted `aiSuggestion` snapshot.
 */
export const AiSuggestionNotes = ({ summary, warnings }: ModelAiSuggestion) => {
  const { t } = useTranslation(["model", "common"]);
  const [open, setOpen] = useState(false);
  if (!summary && warnings.length === 0) return null;
  const topSeverity =
    SEVERITY_ORDER.find((severity) =>
      warnings.some((w) => w.severity === severity),
    ) ?? SuggestionWarningSeverityEnum.INFO;
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex cursor-pointer items-center gap-2 self-start"
        aria-expanded={open}
      >
        <Sparkles className="size-4 text-emerald-600" />
        <span className="text-sm font-semibold text-gray-900">
          {t("aiNotes.title")}
        </span>
        {warnings.length > 0 && (
          <span
            className={cn(
              "rounded-full px-2 text-xs font-medium leading-5",
              COUNT_BADGE_STYLES[topSeverity],
            )}
          >
            {t("aiNotes.warnings", { count: warnings.length })}
          </span>
        )}
        <ChevronDown
          className={cn(
            "size-4 text-black/60 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <>
          {summary && <p className="text-sm text-black/60">{summary}</p>}
          {warnings.map((warning, i) => (
            <WarningAlert key={i} warning={warning} />
          ))}
        </>
      )}
    </div>
  );
};
